import { Button } from '@design/components/button/Button';
import { ButtonLink } from '@design/components/button-link/ButtonLink';
import { TableCell } from '@design/components/table';
import clsx from 'clsx';
import { AnimationEvent, MouseEvent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TopCompetitors.module.scss';
import { COLUMN_COUNT } from './utils';

export interface ExpandableCellProps {
  name: string;
  isExpanded: boolean;
  insightSummary?: string;
  hasEarningsInsight: boolean;
  onExpand: () => void;
  onReadInsightClick: (event: MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
}

export const ExpandableCell = ({
  name,
  isExpanded,
  insightSummary,
  hasEarningsInsight,
  onExpand,
  onReadInsightClick,
  children,
}: ExpandableCellProps) => {
  const { t } = useTranslation(['earnings']);
  const [wasExpanded, setWasExpanded] = useState(isExpanded);
  const [isClosing, setIsClosing] = useState(false);

  if (wasExpanded !== isExpanded) {
    setWasExpanded(isExpanded);
    setIsClosing(
      !isExpanded &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  const handleRowClick = () => {
    onExpand();
  };

  const handleChevronClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onExpand();
  };

  const handleAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || !isClosing) {
      return;
    }

    setIsClosing(false);
  };

  const isDetailVisible = isExpanded || isClosing;

  return (
    <>
      <tr
        className={clsx(styles.competitorRow, {
          [styles.expanded]: isDetailVisible,
        })}
        onClick={handleRowClick}
        data-testid="top-competitors-row"
      >
        <TableCell
          className={clsx(styles.cell, styles.expandColumn)}
          alignment="center"
          isNumber={false}
        >
          <Button
            variant="empty"
            size="xsmall"
            iconName={isDetailVisible ? 'ChevronLightUp' : 'ChevronLightDown'}
            aria-label={t(
              isDetailVisible
                ? 'earnings:peerComparison.topCompetitors.collapseRow'
                : 'earnings:peerComparison.topCompetitors.expandRow',
              { name }
            )}
            aria-expanded={isDetailVisible}
            onClick={handleChevronClick}
          />
        </TableCell>
        {children}
      </tr>
      {isDetailVisible && (
        <tr className={styles.detailRow}>
          <TableCell
            className={styles.detailCell}
            colspan={COLUMN_COUNT}
            alignment="start"
            isNumber={false}
          >
            <div
              className={clsx(styles.detail, {
                [styles.detailOpen]: isExpanded,
                [styles.detailClosing]: isClosing,
              })}
              onAnimationEnd={handleAnimationEnd}
            >
              <div className={styles.detailContent}>
                <div className={styles.detailInner}>
                  {insightSummary ? (
                    <p className={styles.insightSummary}>{insightSummary}</p>
                  ) : (
                    <p
                      className={clsx(
                        styles.insightSummary,
                        styles.placeholder
                      )}
                    >
                      {t('earnings:noDataToDisplay')}
                    </p>
                  )}
                  {hasEarningsInsight && (
                    <ButtonLink
                      label={t(
                        'earnings:peerComparison.topCompetitors.readEarningsInsight'
                      )}
                      onClick={onReadInsightClick}
                    />
                  )}
                </div>
              </div>
            </div>
          </TableCell>
        </tr>
      )}
    </>
  );
};
